import { Select, Flex, Segmented, DatePicker, Button, Typography } from 'antd';

import Icon from '@ant-design/icons';
import { useEffect, useState } from 'react';

import dayjs, { Dayjs } from 'dayjs';
import { countingNumberOfDays } from '../../helpers/mixins';

const { RangePicker } = DatePicker;

interface SelectionOption {
  label: string;
  value: string;
  disabled: boolean;
}

interface AnalyticsCardDateFilterProps {
  pickerStartInputId: string;
  pickerEndInputId: string;
  filteredDates: [Dayjs | null, Dayjs | null];
  setFilteredDates: (dates: [Dayjs | null, Dayjs | null]) => void;
  dates: [Dayjs | null, Dayjs | null];
  setDates: (dates: [Dayjs | null, Dayjs | null]) => void;
  interval?: string;
  setInterval?: (interval: string) => void;
  minDate?: string;
  notTransaction: boolean;
}

export const AnalyticsCardDateFilter = ({
  pickerStartInputId,
  pickerEndInputId,
  filteredDates,
  setFilteredDates,
  dates,
  setDates,
  interval,
  setInterval,
  minDate,
  notTransaction,
}: AnalyticsCardDateFilterProps) => {
  const CalendarSvg = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M15.75 7.5H2.25M12 1.5V4.5M6 1.5V4.5M5.85 16.5H12.15C13.4101 16.5 14.0402 16.5 14.5215 16.2548C14.9448 16.039 15.289 15.6948 15.5048 15.2715C15.75 14.7902 15.75 14.1601 15.75 12.9V6.6C15.75 5.33988 15.75 4.70982 15.5048 4.22852C15.289 3.80516 14.9448 3.46095 14.5215 3.24524C14.0402 3 13.4101 3 12.15 3H5.85C4.58988 3 3.95982 3 3.47852 3.24524C3.05516 3.46095 2.71095 3.80516 2.49524 4.22852C2.25 4.70982 2.25 5.33988 2.25 6.6V12.9C2.25 14.1601 2.25 14.7902 2.49524 15.2715C2.71095 15.6948 3.05516 16.039 3.47852 16.2548C3.95982 16.5 4.58988 16.5 5.85 16.5Z"
        stroke="#414651"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
  console.log('interval', interval);
  const displayFormat = 'DD/MM/YYYY';
  const rangeFormat = 'MMM DD, YYYY';
  const parsedMinDate = minDate ? dayjs(minDate) : null;

  const last7Days = dayjs().subtract(6, 'day');
  const last30Days = dayjs().subtract(29, 'day');
  const last90Days = dayjs().subtract(89, 'day');
  const allTime = parsedMinDate || dayjs().subtract(1, 'year');

  const [selectedSegment, setSelectedSegment] = useState<string | null>('7d');
  const [hasSelectedRange, setHasSelectedRange] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [segmentOptions, setSegmentOptions] = useState<SelectionOption[]>([
    {
      label: '7d',
      value: '7d',
      disabled: false,
    },
    {
      label: '30d',
      value: '30d',
      disabled: false,
    },
    {
      label: '90d',
      value: '90d',
      disabled: false,
    },
    {
      label: 'All time',
      value: 'all',
      disabled: false,
    },
  ]);

  const [intervalOptions, setIntervalOptions] = useState<SelectionOption[]>([
    {
      label: 'Daily',
      value: 'Daily',
      disabled: false,
    },
    {
      label: 'Weekly',
      value: 'Weekly',
      disabled: false,
    },
    {
      label: 'Monthly',
      value: 'Monthly',
      disabled: false,
    },
    {
      label: 'Yearly',
      value: 'Yearly',
      disabled: false,
    },
  ]);

  useEffect(() => {
    const numberOfDays = countingNumberOfDays(filteredDates[0] || dayjs(), filteredDates[1] || dayjs());

    const intervalRules = [
      {
        range: { min: 0, max: 14 },
        disabledValues: ['Weekly', 'Monthly', 'Yearly'], // daily
      },
      {
        range: { min: 14, max: 30 },
        disabledValues: ['Monthly', 'Yearly'], // weekly, daily
      },
      {
        range: { min: 30, max: 90 },
        disabledValues: ['Yearly'], // monthly, weekly, daily
      },
      {
        range: { min: 90, max: 365 },
        disabledValues: ['Daily', 'Yearly'], // monthly, weekly
      },
      {
        range: { min: 365, max: 365 * 3 },
        disabledValues: ['Daily', 'Weekly'], // monthly, yearly
      },
      {
        range: { min: 365 * 3, max: Infinity },
        disabledValues: ['Daily', 'Weekly', 'Monthly'], // yearly
      },
    ];

    const matchedRule = intervalRules.find((rule) => numberOfDays > rule.range.min && numberOfDays <= rule.range.max);

    const parsedIntervalOptions = intervalOptions.map((option) => ({
      ...option,
      disabled: matchedRule?.disabledValues.includes(option.value) || false,
    }));

    setIntervalOptions(parsedIntervalOptions);

    const enabledOptions = parsedIntervalOptions.filter((option) => !option.disabled);
    if (enabledOptions.length > 0) {
      setInterval?.(enabledOptions[0].value);
    }
  }, [filteredDates]);

  const handleDatePickerOpen = () => {
    if (notTransaction) return;
    setDatePickerOpen(true);
    setHasSelectedRange(false);
    document.getElementById('custom-cell-start' + pickerStartInputId)?.focus();
  };

  const handleCalendarChange = (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string], info: { range?: 'start' | 'end' }) => {
    if (info.range === 'end' && hasSelectedRange && dates?.[0] && dates?.[1]) {
      setDates([dates[1], null]);
      setHasSelectedRange(false);
      document.getElementById('custom-cell-end' + pickerEndInputId)?.focus();
    }
    if (info.range === 'start' && dates?.[0] && dates?.[1]) {
      setDates([dates[0], null]);
    }
  };

  const handleChange = (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => {
    if (!hasSelectedRange && dates?.[0] && dates?.[1]) {
      setHasSelectedRange(true);
      setDates(dates);
    } else if (!dates?.[1]) {
      setDates(dates || [null, null]);
    }
  };

  const handleSegmentChange = (value: string) => {
    setSelectedSegment(value);
    if (value === '7d') {
      setDates([last7Days, dayjs()]);
      setFilteredDates([last7Days, dayjs()]);
    } else if (value === '30d') {
      setDates([last30Days, dayjs()]);
      setFilteredDates([last30Days, dayjs()]);
    } else if (value === '90d') {
      setDates([last90Days, dayjs()]);
      setFilteredDates([last90Days, dayjs()]);
    } else if (value === 'all') {
      setDates([allTime, dayjs()]);
      setFilteredDates([allTime, dayjs()]);
    }
  };

  const isLastDays = (start: Dayjs | null, end: Dayjs | null, days: number): boolean => {
    if (!start || !end) return false;

    const today = dayjs();
    const daysAgo = today.subtract(days, 'day');

    return start.isSame(daysAgo, 'day') && end.isSame(today, 'day');
  };

  return (
    <Flex
      gap={16}
      style={{ marginRight: -16 }}
    >
      {interval && setInterval ? (
        <Select
          value={interval}
          style={{ width: 100 }}
          onChange={(value) => {
            setInterval(value);
          }}
          options={intervalOptions}
          disabled={notTransaction}
        />
      ) : null}
      <Flex>
        <Segmented
          options={notTransaction ? segmentOptions.map((option) => ({ ...option, disabled: true })) : segmentOptions}
          value={selectedSegment}
          onChange={(value) => {
            handleSegmentChange(value || '');
          }}
        />
      </Flex>
      <RangePicker
        className="custom-date-picker"
        popupClassName="custom-date-picker-popup"
        format={displayFormat}
        inputReadOnly
        renderExtraFooter={() => {
          return (
            <Flex
              gap={12}
              style={{ padding: '12px' }}
            >
              <Button
                style={{ flex: 1 }}
                onClick={() => {
                  setDatePickerOpen(false);
                  setDates(filteredDates);
                }}
              >
                Cancel
              </Button>
              <Button
                color="cyan"
                variant="solid"
                style={{ flex: 1 }}
                onClick={() => {
                  setDatePickerOpen(false);
                  setFilteredDates(dates);
                  const isSevenDayRange = isLastDays(dates[0], dates[1], 7);
                  const isThirtyDayRange = isLastDays(dates[0], dates[1], 30);
                  const isNinetyDayRange = isLastDays(dates[0], dates[1], 90);
                  const isAllTimeRange = dates[0] === parsedMinDate && dates[1] === dayjs();

                  if (isSevenDayRange) {
                    setSelectedSegment('7d');
                  } else if (isThirtyDayRange) {
                    setSelectedSegment('30d');
                  } else if (isNinetyDayRange) {
                    setSelectedSegment('90d');
                  } else if (isAllTimeRange) {
                    setSelectedSegment('all');
                  } else {
                    setSelectedSegment(null);
                  }
                }}
              >
                Apply
              </Button>
            </Flex>
          );
        }}
        allowClear
        open={datePickerOpen}
        id={{
          start: 'custom-cell-start' + pickerStartInputId,
          end: 'custom-cell-end' + pickerEndInputId,
        }}
        onOpenChange={(status) => {
          setDatePickerOpen(true);
        }}
        onCalendarChange={handleCalendarChange}
        onChange={handleChange}
        maxDate={dayjs()}
        minDate={parsedMinDate || undefined}
        value={dates}
      />
    </Flex>
  );
};
